// src/pages/Project.tsx
//
// The /projects route (also served at /view-my-work).
//
// Replaces a `repeat(auto-fit, minmax(300px, 1fr))` grid of title-and-description
// cards that read from siteConfig.projects. That version is gone, including its
// glassmorphism backdrop-filter, its centred 2.5rem <h2> and a
// screen-reader-hostile `key={index}`.
//
// The page is deliberately two parts: an introduction, and the project grid.
// Every project renders from siteConfig.projects through one ProjectCard, so
// there is no per-project JSX and no project named in this file.
//
// Visual reference is the Home page, specifically the "Selected work" section
// (components/SelectedWorkPreview): same 90%/1120px container, same 4.5rem ->
// 6rem section padding, same mono eyebrow, same heading scale, same hairline
// technology tags, same accent-border hover. Nothing here introduces a token,
// radius, easing or breakpoint that the Home page does not already use.
//
// No closing CTA section. Home ends with ProjectCallToAction and every other
// route ends with Footer, whose own call to action already opens the shared
// Query window, so a second CTA here would be a third link to the same place.
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

import { STAGGER, motionSet } from '../components/about/motion';
import ProjectGrid from '../components/projects/ProjectGrid';
import Footer from '../components/Footer';
import { minDevice } from '../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

const Page = styled.main`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  /* The Navbar is sticky rather than fixed, so the page flows under it and
     needs no offset. Matches the 4.5rem -> 6rem rhythm of the Home sections. */
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

const Header = styled(motion.header)`
  margin-bottom: 2.75rem;
`;

const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

/** h1: this is the page's own heading, not a section heading under another. */
const Title = styled(motion.h1)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

const Lede = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

const headingId = 'projects-heading';
const viewport = { once: true, amount: 0.3 } as const;

const Projects = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  return (
    <>
      <Page>
        <Header
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={anim.reveal}>Professional work</Eyebrow>
          <Title id={headingId} variants={anim.reveal}>
            Projects
          </Title>
          <Lede variants={anim.reveal}>
            Client software I have helped build and support across energy, enterprise IT and
            aviation — backend services, integrations and the data work behind them.
          </Lede>
        </Header>

        <ProjectGrid />
      </Page>

      <Footer />
    </>
  );
};

export default Projects;
